import { ProjectPathParams, Paths } from '../../../app/router/app.router';
import { useDeliveriesList } from './hooks/use-deliveries-list.hook';
import { useMemo } from 'react';
import { RestError } from '@azure/core-http';
import { ErrorFullPage } from '../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { makeDeliveriesListPageHeaderProps } from './logic/deliveries-list-props.map';
import { WindowTitle } from '../../../core/window-title/window-title.component';
import { ListPageHeader } from '../../../ui/components/pure/page-headers/list/list-page-header.component';
import { DeliveriesTable } from './components/deliveries-table.component';
import { DeliveriesListActions } from './actions/deliveries-list.actions';
import { DeliveriesListActionsObserver } from './deliveries-list-actions-observer';
import { Container } from '@mui/material';

const MAIN_COLUMNS: { key: string; label: string }[] = [
  { key: 'project', label: 'PROJECT' },
  { key: 'hub', label: 'HUB' },
  { key: 'type', label: 'TYPE' },
  { key: 'by', label: 'BY' },
  { key: 'agent', label: 'AGENT' },
  { key: 'deliveryTime', label: 'DELIVERY STARTED TIME' },
  { key: 'to', label: 'TO' },
  { key: 'unit', label: 'UNIT' },
  { key: 'completedTime', label: 'COMPLETED TIME' },
  { key: 'channel', label: 'CHANNEL' },
  { key: 'status', label: 'STATUS' },
  { key: 'notifications', label: 'NOTIFICATIONS' }
];

export const DeliveriesListPage = ({ params }: { params: ProjectPathParams<Paths.deliveries> }) => {
  const {
    project,
    deliveries,
    infiniteScroll,
    formData,
    setFormData,
    hubFilter,
    carrierFilter,
    showHubNameInTable,
    handlers,
    state,
    showReceiveDeliveryButton
  } = useDeliveriesList(params.projectID);

  const sortData = useMemo(
    () => ({ sortBy: formData.sortBy, sortOrder: formData.sortOrder }),
    [formData.sortBy, formData.sortOrder]
  );

  const error = deliveries.error;
  if (error) {
    if ((error as RestError).statusCode === 401) return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
    if ((error as RestError).statusCode === 403) return <ErrorFullPage preset="ACCESS_DENIED" />;
    if ((error as RestError).statusCode === 404) return <ErrorFullPage preset="NOT_FOUND" />;
    return <ErrorFullPage />;
  }

  const headerProps = makeDeliveriesListPageHeaderProps(
    params.projectID,
    deliveries.data?.pages[0]?.totalRecords || 0,
    () => handlers.setAction(DeliveriesListActions.CREATE_DELIVERY),
    formData,
    setFormData,
    hubFilter,
    carrierFilter,
    showReceiveDeliveryButton
  );

  return (
    <Container>
      {project.data ? <WindowTitle pageTitle="Deliveries" detail={project.data.name} /> : undefined}
      <ListPageHeader {...headerProps} />
      <DeliveriesTable
        viewId="main"
        columns={MAIN_COLUMNS}
        params={params}
        pages={deliveries.data?.pages || []}
        infiniteScroll={infiniteScroll}
        setFormData={setFormData}
        sortData={sortData}
        isLoading={deliveries.isLoading}
        isPreviousData={deliveries.isPreviousData}
        hasError={deliveries.isError}
        showHubName={showHubNameInTable}
      />
      <DeliveriesListActionsObserver projectID={params.projectID} handlers={handlers} state={state} />
    </Container>
  );
};
